import { useState, useEffect, useCallback } from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useAuth } from '../../contexts/AuthContext'
import { formatSydneyDate } from '../../utils/sydneyDateTime'
import {
  DocumentTextIcon,
  PencilSquareIcon,
  LinkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'

function formatEffectiveDate(value) {
  if (!value) return null
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return String(value).slice(0, 10)
    return formatSydneyDate(d)
  } catch {
    return String(value).slice(0, 10)
  }
}

const TABS = [
  { id: 'published', label: 'Published rules', icon: DocumentTextIcon },
  { id: 'edit', label: 'Edit draft', icon: PencilSquareIcon },
  { id: 'mapping', label: 'Rule mapping', icon: LinkIcon },
]

function canEditGamingRules(can) {
  return (
    can('governance-safe/gaming-rules', 'edit') ||
    can('governance-safe/obligations', 'manage')
  )
}

function SectionBlock({
  section,
  editable,
  onSaveClause,
  onSaveSectionTitle,
  onAddClause,
  savingId,
  savingSectionKey,
  addingClauseKey,
}) {
  const [title, setTitle] = useState(section.section_title || '')
  const titleDirty = title !== (section.section_title || '')

  useEffect(() => {
    setTitle(section.section_title || '')
  }, [section.section_title])

  return (
    <div className="mb-8">
      {editable ? (
        <div className="mb-3">
          <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
            Section heading
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="flex-1 min-w-[200px] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm font-semibold uppercase tracking-wide p-2"
            />
            {titleDirty && (
              <button
                type="button"
                disabled={savingSectionKey === section.section_key}
                onClick={() => onSaveSectionTitle(section.section_key, title)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-violet-600 text-white text-xs font-medium hover:bg-violet-700 disabled:opacity-50"
              >
                {savingSectionKey === section.section_key ? 'Saving…' : 'Save heading'}
              </button>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-1 font-mono">{section.section_key}</p>
        </div>
      ) : (
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-3">
          {section.section_title}
        </h3>
      )}
      <ul className="space-y-3">
        {section.clauses.map((clause) => (
          <li
            key={clause.id || clause.clause_code}
            className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 p-4"
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <span className="text-xs font-mono text-violet-600 dark:text-violet-400">{clause.clause_code}</span>
            </div>
            {editable ? (
              <ClauseEditor
                clause={clause}
                onSave={onSaveClause}
                saving={savingId === clause.id}
              />
            ) : (
              <p className="text-sm text-slate-800 dark:text-slate-200 leading-relaxed">{clause.body_text}</p>
            )}
          </li>
        ))}
      </ul>
      {editable && (
        <AddClauseForm
          sectionKey={section.section_key}
          loading={addingClauseKey === section.section_key}
          onAdd={(bodyText) => onAddClause(section.section_key, bodyText)}
        />
      )}
    </div>
  )
}

function AddClauseForm({ loading, onAdd }) {
  const [text, setText] = useState('')
  const [open, setOpen] = useState(false)

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-violet-600 dark:text-violet-400 hover:underline"
      >
        <PlusIcon className="w-4 h-4" /> Add rule to this section
      </button>
    )
  }

  return (
    <div className="mt-3 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 p-3">
      <label className="block text-xs font-medium text-slate-500 mb-1">New rule text</label>
      <textarea
        className="w-full min-h-[72px] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm p-3"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Enter patron-facing rule wording…"
      />
      <div className="flex gap-2 mt-2">
        <button
          type="button"
          disabled={!text.trim() || loading}
          onClick={async () => {
            await onAdd(text.trim())
            setText('')
            setOpen(false)
          }}
          className="px-3 py-1.5 rounded-md bg-violet-600 text-white text-xs font-medium disabled:opacity-50"
        >
          {loading ? 'Adding…' : 'Add rule'}
        </button>
        <button type="button" onClick={() => { setOpen(false); setText('') }} className="px-3 py-1.5 text-xs text-slate-500">
          Cancel
        </button>
      </div>
    </div>
  )
}

function AddSectionForm({ loading, onAdd }) {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-300 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
      >
        <PlusIcon className="w-4 h-4" /> Add section
      </button>
    )
  }

  return (
    <div className="rounded-lg border border-dashed border-violet-300 dark:border-violet-700 bg-violet-50/50 dark:bg-violet-900/10 p-4">
      <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">New section</h4>
      <label className="block text-xs font-medium text-slate-500 mb-1">Section heading</label>
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="e.g. Fair play of gaming machines"
        className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm p-2 mb-3"
      />
      <label className="block text-xs font-medium text-slate-500 mb-1">First rule in this section</label>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Patron-facing rule wording…"
        className="w-full min-h-[72px] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm p-3"
      />
      <div className="flex gap-2 mt-3">
        <button
          type="button"
          disabled={!title.trim() || !body.trim() || loading}
          onClick={async () => {
            await onAdd(title.trim(), body.trim())
            setTitle('')
            setBody('')
            setOpen(false)
          }}
          className="px-4 py-2 rounded-md bg-violet-600 text-white text-sm font-medium disabled:opacity-50"
        >
          {loading ? 'Adding…' : 'Add section'}
        </button>
        <button type="button" onClick={() => { setOpen(false); setTitle(''); setBody('') }} className="px-3 py-2 text-sm text-slate-500">
          Cancel
        </button>
      </div>
    </div>
  )
}

function DocumentFieldsEditor({ document, onSave, saving }) {
  const [title, setTitle] = useState(document?.title || '')
  const [intro, setIntro] = useState(document?.intro_text || '')
  const [footer, setFooter] = useState(document?.footer_text || '')

  useEffect(() => {
    setTitle(document?.title || '')
    setIntro(document?.intro_text || '')
    setFooter(document?.footer_text || '')
  }, [document])

  const dirty =
    title !== (document?.title || '') ||
    intro !== (document?.intro_text || '') ||
    footer !== (document?.footer_text || '')

  return (
    <div className="mb-6 space-y-3 rounded-lg border border-slate-200 dark:border-slate-700 p-4 bg-slate-50/50 dark:bg-slate-800/30">
      <p className="text-xs font-medium text-slate-500">Document text (intro & footer)</p>
      <div>
        <label className="block text-xs text-slate-500 mb-1">Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm p-2"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-1">Introduction</label>
        <textarea
          value={intro}
          onChange={(e) => setIntro(e.target.value)}
          className="w-full min-h-[72px] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm p-3"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-1">Footer</label>
        <textarea
          value={footer}
          onChange={(e) => setFooter(e.target.value)}
          className="w-full min-h-[56px] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm p-3"
        />
      </div>
      {dirty && (
        <button
          type="button"
          disabled={saving}
          onClick={() => onSave({ title, intro_text: intro, footer_text: footer })}
          className="px-3 py-1.5 rounded-md bg-violet-600 text-white text-xs font-medium disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save document text'}
        </button>
      )}
    </div>
  )
}

function ClauseEditor({ clause, onSave, saving }) {
  const [text, setText] = useState(clause.body_text || '')
  const dirty = text !== (clause.body_text || '')

  return (
    <div>
      <textarea
        className="w-full min-h-[80px] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm p-3"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {dirty && (
        <button
          type="button"
          disabled={saving}
          onClick={() => onSave(clause.id, text)}
          className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-violet-600 text-white text-xs font-medium hover:bg-violet-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save clause'}
        </button>
      )}
    </div>
  )
}

function MappingTab({ canEdit, clauseOptions }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [mapped, setMapped] = useState([])
  const [unmapped, setUnmapped] = useState([])
  const [clauseLabels, setClauseLabels] = useState({})
  const [savingRule, setSavingRule] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await authFetch(getLocalApiUrl('/api/governance/gaming-rules/mappings'))
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to load mappings')
      setMapped(j.data.mapped || [])
      setUnmapped(j.data.unmapped || [])
      setClauseLabels(j.data.clause_labels || {})
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const allRows = [...mapped, ...unmapped].sort((a, b) =>
    String(a.rule_name || a.rule_id).localeCompare(String(b.rule_name || b.rule_id))
  )

  const saveMapping = async (ruleId, clauseCode) => {
    if (!canEdit) return
    setSavingRule(ruleId)
    try {
      const mappings = clauseCode
        ? [{ clause_code: clauseCode, is_primary: true }]
        : []
      const res = await authFetch(
        getLocalApiUrl(`/api/governance/gaming-rules/mappings/${encodeURIComponent(ruleId)}`),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mappings }),
        }
      )
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Save failed')
      await load()
    } catch (e) {
      alert(e.message)
    } finally {
      setSavingRule(null)
    }
  }

  if (loading) {
    return <p className="text-sm text-slate-500">Loading rule mappings…</p>
  }
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-800 dark:text-red-200">
        {error}
      </div>
    )
  }

  const options = clauseOptions.length
    ? clauseOptions
    : Object.entries(clauseLabels).map(([code, body]) => ({ code, label: body }))

  return (
    <div>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
        Map each monitor rule to a patron-safe gaming rule clause. Staff and patron communications must cite these clauses — not AML rule names or typologies.
      </p>
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/80">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Monitor rule</th>
              <th className="text-left px-4 py-3 font-semibold">Patron clause</th>
              {canEdit && <th className="text-left px-4 py-3 font-semibold w-48">Set primary</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {allRows.map((row) => {
              const primary = row.mappings?.find((m) => m.is_primary) || row.mappings?.[0]
              const currentCode = primary?.clause_code || ''
              return (
                <tr key={row.rule_id} className="bg-white dark:bg-slate-900/30">
                  <td className="px-4 py-3 align-top">
                    <div className="font-medium text-slate-900 dark:text-slate-100">{row.rule_name || row.rule_id}</div>
                    <div className="text-xs font-mono text-slate-500 mt-0.5">{row.rule_id}</div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    {primary ? (
                      <div>
                        <span className="text-xs font-mono text-violet-600 dark:text-violet-400">{primary.clause_code}</span>
                        <p className="text-slate-700 dark:text-slate-300 mt-1">
                          {clauseLabels[primary.clause_code] || '—'}
                        </p>
                      </div>
                    ) : (
                      <span className="text-amber-600 dark:text-amber-400 text-xs inline-flex items-center gap-1">
                        <ExclamationTriangleIcon className="w-4 h-4" /> Not mapped
                      </span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3 align-top">
                      <select
                        className="w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs p-2"
                        value={currentCode}
                        disabled={savingRule === row.rule_id}
                        onChange={(e) => saveMapping(row.rule_id, e.target.value)}
                      >
                        <option value="">— None / remove —</option>
                        {options.map((o) => (
                          <option key={o.code} value={o.code}>
                            {o.code}
                          </option>
                        ))}
                      </select>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function GamingRulesOfPlay() {
  const { can } = useAuth()
  const canEdit = canEditGamingRules(can)
  const [tab, setTab] = useState('published')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [published, setPublished] = useState(null)
  const [draftMeta, setDraftMeta] = useState(null)
  const [draft, setDraft] = useState(null)
  const [savingId, setSavingId] = useState(null)
  const [savingSectionKey, setSavingSectionKey] = useState(null)
  const [addingClauseKey, setAddingClauseKey] = useState(null)
  const [addingSection, setAddingSection] = useState(false)
  const [savingDocument, setSavingDocument] = useState(false)
  const [publishing, setPublishing] = useState(false)

  const applyDraftData = (data) => {
    setDraft(data)
    setDraftMeta({ document: data.document, has_changes: true })
  }

  const reloadDraftFromResponse = (sections, documentPatch) => {
    setDraft((prev) => {
      if (!prev) return prev
      const clauses = sections.flatMap((s) => s.clauses)
      return {
        ...prev,
        document: documentPatch ? { ...prev.document, ...documentPatch } : prev.document,
        clauses,
        sections,
      }
    })
  }

  const loadPublished = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await authFetch(getLocalApiUrl('/api/governance/gaming-rules'))
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to load gaming rules')
      setPublished(j.data.published)
      setDraftMeta(j.data.draft)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  const loadDraft = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await authFetch(getLocalApiUrl('/api/governance/gaming-rules/draft'))
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to load draft')
      applyDraftData(j.data)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadPublished()
  }, [loadPublished])

  useEffect(() => {
    if (tab === 'edit' && canEdit) {
      loadDraft()
    }
  }, [tab, canEdit, loadDraft])

  const saveClause = async (clauseId, bodyText) => {
    setSavingId(clauseId)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/governance/gaming-rules/clauses/${encodeURIComponent(clauseId)}`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body_text: bodyText }),
        }
      )
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Save failed')
      setDraft((prev) => {
        if (!prev) return prev
        const clauses = prev.clauses.map((c) => (c.id === clauseId ? { ...c, body_text: bodyText } : c))
        const sections = prev.sections.map((sec) => ({
          ...sec,
          clauses: sec.clauses.map((c) => (c.id === clauseId ? { ...c, body_text: bodyText } : c)),
        }))
        return { ...prev, clauses, sections }
      })
    } catch (e) {
      alert(e.message)
    } finally {
      setSavingId(null)
    }
  }

  const saveSectionTitle = async (sectionKey, sectionTitle) => {
    setSavingSectionKey(sectionKey)
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/governance/gaming-rules/sections/${encodeURIComponent(sectionKey)}`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ section_title: sectionTitle }),
        }
      )
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Save failed')
      reloadDraftFromResponse(j.data.sections)
    } catch (e) {
      alert(e.message)
    } finally {
      setSavingSectionKey(null)
    }
  }

  const addClause = async (sectionKey, bodyText) => {
    setAddingClauseKey(sectionKey)
    try {
      const res = await authFetch(getLocalApiUrl('/api/governance/gaming-rules/clauses'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section_key: sectionKey, body_text: bodyText }),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Add failed')
      reloadDraftFromResponse(j.data.sections)
    } catch (e) {
      alert(e.message)
    } finally {
      setAddingClauseKey(null)
    }
  }

  const addSection = async (sectionTitle, bodyText) => {
    setAddingSection(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/governance/gaming-rules/sections'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section_title: sectionTitle, body_text: bodyText }),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Add failed')
      reloadDraftFromResponse(j.data.sections)
    } catch (e) {
      alert(e.message)
    } finally {
      setAddingSection(false)
    }
  }

  const saveDocument = async (fields) => {
    setSavingDocument(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/governance/gaming-rules/document'), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Save failed')
      setDraft((prev) => (prev ? { ...prev, document: j.data } : prev))
    } catch (e) {
      alert(e.message)
    } finally {
      setSavingDocument(false)
    }
  }

  const publishDraft = async () => {
    if (!window.confirm('Publish draft? The current published version will be archived.')) return
    setPublishing(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/governance/gaming-rules/publish'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Publish failed')
      setPublished({
        document: j.data.document,
        clauses: j.data.clauses,
        sections: j.data.sections,
      })
      setDraft(null)
      setDraftMeta(null)
      setTab('published')
    } catch (e) {
      alert(e.message)
    } finally {
      setPublishing(false)
    }
  }

  const clauseOptions = (published?.clauses || draft?.clauses || []).map((c) => ({
    code: c.clause_code,
    label: c.body_text,
  }))

  const activeDoc = tab === 'edit' && draft ? draft : published
  const sections = activeDoc?.sections || []

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Gaming Rules of Play</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Patron-facing club gaming rules — separate from AML monitor rules. Use these clauses when communicating with members and guests.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200 dark:border-slate-700 pb-2">
        {TABS.map((t) => {
          const Icon = t.icon
          const disabled = t.id === 'edit' && !canEdit
          return (
            <button
              key={t.id}
              type="button"
              disabled={disabled}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-md border-b-2 -mb-[2px] transition-colors ${
                tab === t.id
                  ? 'border-violet-600 text-violet-700 dark:text-violet-300'
                  : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              } ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          )
        })}
      </div>

      {loading && tab !== 'mapping' && (
        <p className="text-sm text-slate-500">Loading…</p>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-800 dark:text-red-200 mb-4">
          {error}
        </div>
      )}

      {tab === 'mapping' && (
        <MappingTab canEdit={canEdit} clauseOptions={clauseOptions} />
      )}

      {tab !== 'mapping' && !loading && !error && activeDoc && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold text-slate-900 dark:text-white">
                  {activeDoc.document?.title || 'Gaming Rules of Play'}
                </span>
                {activeDoc.document?.version_label && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                    {activeDoc.document.version_label}
                  </span>
                )}
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    activeDoc.document?.status === 'published'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                      : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
                  }`}
                >
                  {activeDoc.document?.status}
                </span>
              </div>
              {activeDoc.document?.effective_from && (
                <p className="text-xs text-slate-500 mt-1">
                  Effective from {formatEffectiveDate(activeDoc.document.effective_from)}
                </p>
              )}
            </div>
            {tab === 'edit' && canEdit && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={loadDraft}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-300 dark:border-slate-600 rounded-md"
                >
                  <ArrowPathIcon className="w-4 h-4" /> Refresh draft
                </button>
                <button
                  type="button"
                  disabled={publishing}
                  onClick={publishDraft}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  {publishing ? 'Publishing…' : 'Publish'}
                </button>
              </div>
            )}
          </div>

          {draftMeta && tab === 'published' && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
              A draft exists with unpublished changes.
              {canEdit && (
                <button type="button" className="ml-2 underline font-medium" onClick={() => setTab('edit')}>
                  Continue editing
                </button>
              )}
            </div>
          )}

          {tab === 'edit' && canEdit && (
            <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
              Edit section headings, rules, and intro/footer here. Changes stay in draft until you publish.
            </p>
          )}

          {tab === 'edit' && canEdit && activeDoc.document && (
            <DocumentFieldsEditor
              document={activeDoc.document}
              onSave={saveDocument}
              saving={savingDocument}
            />
          )}

          {activeDoc.document?.intro_text && tab !== 'edit' && (
            <p className="text-sm text-slate-700 dark:text-slate-300 mb-6 leading-relaxed">
              {activeDoc.document.intro_text}
            </p>
          )}

          {sections.map((section) => (
            <SectionBlock
              key={section.section_key}
              section={section}
              editable={tab === 'edit' && canEdit}
              onSaveClause={saveClause}
              onSaveSectionTitle={saveSectionTitle}
              onAddClause={addClause}
              savingId={savingId}
              savingSectionKey={savingSectionKey}
              addingClauseKey={addingClauseKey}
            />
          ))}

          {tab === 'edit' && canEdit && (
            <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
              <AddSectionForm loading={addingSection} onAdd={addSection} />
            </div>
          )}

          {activeDoc.document?.footer_text && tab !== 'edit' && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-8 border-t border-slate-200 dark:border-slate-700 pt-4 italic">
              {activeDoc.document.footer_text}
            </p>
          )}
        </div>
      )}

      {tab !== 'mapping' && !loading && !error && !activeDoc && (
        <p className="text-sm text-slate-500">
          No published Gaming Rules document yet. Run tenant migration 265 or ask an administrator to seed the default template.
        </p>
      )}
    </div>
  )
}
